import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { MulticolorValue } from '@elfsight/configurator-contracts';
import { Check12 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { Tooltip } from '@elfsight/widget-modal';
import { createReadableClass } from '@elfsight/widget-utils';
import { useTranslation } from 'react-i18next';
import type { SourceId } from '../../../constants';
import { getSourceMetaValue, meta } from '../../../constants';
import { getVerifiedBadgeColor, useSocialFeedSettings } from '../../utils';
import { Link } from './Link';

interface VerifiedBadgeProps {
  url?: string;
  color?: MulticolorValue;
}

export function VerifiedBadge({ url, color, ...props }: VerifiedBadgeProps) {
  const { provider } = meta;
  const isLinkedin = provider === 'linkedin';
  const { t } = useTranslation();
  const { tooltipBackgroundColor, tooltipTextColor } = useSocialFeedSettings();

  const { badgeBackground, VerifiedBadgeIcon, verifiedTitle } = useMemo(() => {
    const {
      verifiedBadgeIcon,
      verifiedBadgeSize,
      isVerifiedBadgeMulticolor,
      title
    } = getSourceMetaValue(provider as SourceId);

    return {
      VerifiedBadgeIcon: verifiedBadgeIcon ? (
        <Icon src={verifiedBadgeIcon} size={verifiedBadgeSize ?? 8} />
      ) : (
        <Icon component={Check12} size={verifiedBadgeSize ?? 8} fill="#fff" />
      ),
      badgeBackground: getVerifiedBadgeColor(color, isVerifiedBadgeMulticolor),
      verifiedTitle: title
    };
  }, [color, provider]);

  return (
    <Tooltip
      title={`${t('Verified by')} ${verifiedTitle}`}
      background={tooltipBackgroundColor}
      fontColor={tooltipTextColor}
    >
      <BadgeContainer
        href={url}
        _badgeBackground={badgeBackground}
        _svgColor={isLinkedin ? color?.color : undefined}
        _badgeSize={isLinkedin ? 14 : 12}
        {...props}
      >
        {VerifiedBadgeIcon}
      </BadgeContainer>
    </Tooltip>
  );
}

const BadgeContainer = styled(Link).attrs({
  className: createReadableClass({
    component: 'header-name',
    element: 'badge-container'
  })
})<{
  _badgeBackground: string;
  _svgColor?: string;
  _badgeSize: number;
}>`
  position: relative;
  width: ${({ _badgeSize }) => _badgeSize}px;
  height: ${({ _badgeSize }) => _badgeSize}px;
  background: ${({ _badgeBackground }) => _badgeBackground};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${({ _svgColor }) =>
    _svgColor &&
    css`
      & path {
        fill: ${_svgColor};
      }
    `}
`;
